/**
 * Page Réglages : profil, feedback, déconnexion.
 */

import { renderHeader } from '../components/header.js';
import { openModal, closeModal } from '../components/modal.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import { getUser, isLoggedIn, logout, authFetch } from '../utils/auth.js';
import {
  iconLogOut,
  iconMail,
  iconMessageSquare,
  iconStar,
  iconChevronRight,
} from '../utils/icons.js';

/**
 * @param {HTMLElement} container
 */
export async function renderAccountPage(container) {
  if (!isLoggedIn()) {
    navigate('/login');
    return;
  }

  clearElement(container);

  container.appendChild(renderHeader({
    title: 'Réglages',
    showLogo: false,
  }));

  const user = getUser();
  const content = createElement('div', { className: 'page-content account-page page--with-nav' });

  // Avatar
  let avatarEl;
  if (user?.avatar_url) {
    avatarEl = createElement('img', {
      className: 'account-page__avatar account-page__avatar--img',
      attrs: { src: user.avatar_url, alt: user.pseudo || '', referrerpolicy: 'no-referrer' },
    });
  } else {
    const initial = (user?.pseudo?.[0] || user?.email?.[0] || '?').toUpperCase();
    avatarEl = createElement('div', {
      className: 'account-page__avatar',
      text: initial,
    });
  }

  const infoSection = createElement('div', {
    className: 'account-page__info',
    children: [
      avatarEl,
      createElement('h2', { className: 'account-page__pseudo', text: user?.pseudo || 'Utilisateur' }),
      createElement('p', {
        className: 'account-page__email text-muted',
        children: [iconMail(14), createElement('span', { text: ` ${user?.email || ''}` })],
      }),
    ],
  });

  // Feedback
  const feedbackRow = createElement('button', {
    className: 'account-page__menu-item',
    attrs: { type: 'button' },
    children: [
      createElement('span', { className: 'account-page__menu-icon', children: [iconMessageSquare(20)] }),
      createElement('div', {
        className: 'account-page__menu-text',
        children: [
          createElement('span', { className: 'account-page__menu-label', text: 'Donner mon avis' }),
          createElement('span', { className: 'account-page__menu-desc', text: 'Aidez-nous à améliorer GroceWise' }),
        ],
      }),
      createElement('span', { className: 'account-page__menu-chevron', children: [iconChevronRight(16)] }),
    ],
    events: {
      click: () => openFeedbackModal(),
    },
  });

  const menuList = createElement('div', {
    className: 'account-page__menu',
    children: [feedbackRow],
  });

  const logoutBtn = createElement('button', {
    className: 'btn btn--danger btn--block account-page__action',
    children: [iconLogOut(18), createElement('span', { text: ' Se déconnecter' })],
    events: {
      click: () => {
        logout();
        showToast('Déconnecté', 2000, 'success');
        navigate('/');
      },
    },
  });

  content.appendChild(createElement('div', {
    className: 'account-page__container',
    children: [infoSection, menuList, logoutBtn],
  }));

  container.appendChild(content);
}

/**
 * Ouvre la modale de feedback (note + commentaire).
 */
function openFeedbackModal() {
  let selectedRating = 0;

  const starsContainer = createElement('div', { className: 'feedback__stars' });

  function updateStars(rating) {
    selectedRating = rating;
    starsContainer.querySelectorAll('.feedback__star').forEach((star, i) => {
      star.classList.toggle('feedback__star--active', i < rating);
    });
  }

  for (let i = 1; i <= 5; i++) {
    const star = createElement('button', {
      className: 'feedback__star',
      attrs: { type: 'button', 'aria-label': `${i} étoile${i > 1 ? 's' : ''}` },
      children: [iconStar(28)],
      events: { click: () => updateStars(i) },
    });
    starsContainer.appendChild(star);
  }

  const commentInput = createElement('textarea', {
    className: 'input feedback__comment',
    attrs: {
      placeholder: 'Qu\'aimeriez-vous améliorer ? Des idées de fonctionnalités ?',
      rows: '4',
    },
  });

  const form = createElement('div', {
    className: 'feedback__form',
    children: [
      createElement('label', { className: 'input-group__label', text: 'Votre note' }),
      starsContainer,
      createElement('label', {
        className: 'input-group__label',
        attrs: { style: 'margin-top: var(--space-md)' },
        text: 'Votre avis (optionnel)',
      }),
      commentInput,
    ],
  });

  const submitBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Envoyer',
    events: {
      click: async () => {
        if (selectedRating === 0) {
          showToast('Sélectionnez une note', 3000, 'error');
          return;
        }
        try {
          await authFetch('/feedback', {
            method: 'POST',
            body: JSON.stringify({
              rating: selectedRating,
              comment: commentInput.value.trim(),
            }),
          });
          closeModal();
          showToast('Merci pour votre avis !', 3000, 'success');
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Donner mon avis',
    content: form,
    footer: submitBtn,
  });
}
